import { FC, ReactNode, useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { postSkyddsklassningSbm } from 'store/aktivitetSlice';
import { clearBebyggelseobjekt, getBebyggelseobjekt } from 'store/bebyggelseobjektSlice';
import { setMapMode } from 'store/mapSlice';
import { RootState } from 'store/rootReducer';

import { useAppDispatch, useAppSelector } from 'hooks/defaultStoreHooks';
import { useDrawerMobileTemplate } from 'hooks/useDrawerMobileTemplate';
import { useInitialFocusOnExtent } from 'hooks/useInitialFocusOnExtent';

import { RaaDatePicker, RaaSpinner } from 'raa-react';

import {
  SkyddsklassningFormValues,
  skyddsklassningFormSchema
} from 'components/aktivitetRegistrering/skyddsklassning/skyddsklassningFormSchema';
import {
  DrawerBody,
  DrawerContainer,
  DrawerFooter,
  DrawerSection,
  NavigationDrawerHead,
  SpinnerContainer
} from 'components/drawerSections/DrawerSections';
import RaaInputField from 'components/fieldComponents/raaInputField/RaaInputField';
import { replaceBebyggelseobjektFeaturesInActiveSource } from 'components/map/Layers';

import { SkyddsklassningSbmPost } from 'models';

import { MapModeEnum, SkyddsklassningstypEnum } from 'enums';

import dayjs from 'dayjs';
import { Form, Formik, FormikHelpers } from 'formik';
import { setApiValidationErrors } from 'utilities/formUtilities';
import { getIngaendeAsPublicerade } from 'utilities/getIngaendeAsPublicerade';

interface SkyddsklassningRegistrationProps {
  skyddsklassningstyp: SkyddsklassningstypEnum;
}

const SkyddsklassningRegistration: FC<SkyddsklassningRegistrationProps> = ({ skyddsklassningstyp }): ReactNode => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [initialValues] = useState<SkyddsklassningFormValues>({ beslutsdatum: null, diarienummer: '' });
  const [dirtyForm, setDirtyForm] = useState<boolean>(false);

  const { requestStatus: domanerRequestStatus, data: domaner } = useAppSelector(
    (state: RootState) => state.aktivitetDomaner
  );
  const { requestStatus: bebyggelseobjectPubliceradRequestStatus, data: bebyggelseobjektPublicerad } = useAppSelector(
    (state: RootState) => state.bebyggelseobjekt
  );

  const { requestStatus: aktivitetRequestStatus } = useAppSelector((state: RootState) => state.aktivitet);
  const { id } = useParams();

  const { setDrawerMobileTemplate } = useDrawerMobileTemplate();

  useEffect(() => {
    if (id) {
      dispatch(getBebyggelseobjekt(id));
    } else {
      dispatch(clearBebyggelseobjekt());
      replaceBebyggelseobjektFeaturesInActiveSource([]);
      dispatch(setMapMode(MapModeEnum.DEFAULT));
    }

    //Cleanup
    return () => {
      setDrawerMobileTemplate(undefined);
      dispatch(clearBebyggelseobjekt());
      replaceBebyggelseobjektFeaturesInActiveSource([]);
      dispatch(setMapMode(MapModeEnum.DEFAULT));
    };
  }, [id, dispatch, setDrawerMobileTemplate]);

  useEffect(() => {
    setDrawerMobileTemplate(
      <div className="tw-flex tw-flex-col tw-gap-2 tw-p-4">
        <h2 className="tw-heading-md tw-w-64 tw-truncate md:tw-w-full">
          {domaner?.skyddsklassningsaktiviteter.find((typ) => typ.id === skyddsklassningstyp)?.namn}
        </h2>
        <div className="tw-flex tw-items-center tw-justify-start">
          <p>Aktivitetsnummer: Ej sparat</p>
        </div>
      </div>
    );
  }, [domaner?.skyddsklassningsaktiviteter, setDrawerMobileTemplate, skyddsklassningstyp]);

  useInitialFocusOnExtent(
    useCallback(
      () =>
        bebyggelseobjektPublicerad &&
        replaceBebyggelseobjektFeaturesInActiveSource([
          bebyggelseobjektPublicerad,
          ...getIngaendeAsPublicerade(bebyggelseobjektPublicerad)
        ]),
      [bebyggelseobjektPublicerad]
    )
  );

  const submitForm = async (
    values: SkyddsklassningFormValues,
    { setFieldError, resetForm }: FormikHelpers<SkyddsklassningFormValues>
  ) => {
    if (domaner?.beslutsorganisationer_sbm[0].organisationsnummer) {
      const post: SkyddsklassningSbmPost = {
        beslutandeorganisationsnummer: domaner.beslutsorganisationer_sbm[0].organisationsnummer,
        beslutsdatum: dayjs(values.beslutsdatum).format('YYYY-MM-DD') || '',
        diarienummer: values.diarienummer,
        skyddsklassningstyp_id: skyddsklassningstyp
      };
      try {
        const action = await dispatch(postSkyddsklassningSbm({ post, bebyggelseobjektId: id })).unwrap();
        resetForm();
        navigate(`/aktivitet/${action.aktivitet_id}`);
      } catch (error: any) {
        setApiValidationErrors(error, setFieldError);
      }
    }
  };

  return (
    <SpinnerContainer
      showSpinner={domanerRequestStatus === 'pending' || bebyggelseobjectPubliceradRequestStatus === 'pending'}
    >
      {initialValues && (
        <Formik
          initialValues={initialValues}
          validationSchema={skyddsklassningFormSchema}
          onSubmit={submitForm}
          innerRef={(ref) => {
            setDirtyForm(ref?.dirty || false);
          }}
        >
          {({ errors, touched, setFieldValue, values, isValid, setFieldTouched, validateField }) => (
            <Form id="skyddsklassningForm" data-testid="skyddsklassning-registration-form" className="tw-h-full">
              <DrawerContainer>
                <NavigationDrawerHead disableBackNavigation={false} />
                <DrawerBody>
                  <DrawerSection>
                    <h1 className="tw-heading-lg tw-mb-6">
                      {domaner?.skyddsklassningsaktiviteter.find((typ) => typ.id === skyddsklassningstyp)?.namn}
                    </h1>
                    <p className="tw-mb-4">
                      {domaner?.skyddsklassningsaktiviteter.find((typ) => typ.id === skyddsklassningstyp)?.beskrivning}
                    </p>
                    <ul>
                      <li>
                        <span className="tw-font-semibold">Aktivitetsnummer: </span>Ej sparat
                      </li>
                      <li>
                        <span className="tw-font-semibold">Utförande organisation: </span>Riksantikvarieämbetet
                      </li>
                      <li>
                        <span className="tw-font-semibold">Publicerat: </span>Ej sparat
                      </li>
                    </ul>
                  </DrawerSection>
                  <DrawerSection>
                    <p className="tw-mb-4">
                      <strong>Beslutsorganisation: </strong>
                      {domaner?.beslutsorganisationer_sbm[0].organisationsnamn}
                    </p>
                    <fieldset className="tw-flex tw-gap-4">
                      <div className="tw-w-1/2">
                        <label className="raa-label" htmlFor="beslutsdatum">
                          Beslutsdatum
                        </label>
                        <div>
                          <RaaDatePicker
                            id="beslutsdatum"
                            placeholder="åååå-mm-dd"
                            defaultValue={values.beslutsdatum ? dayjs(values.beslutsdatum) : undefined}
                            onChange={async (value) => {
                              const dayjsValue = dayjs(value, 'YYYY-MM-DD').toString();
                              await setFieldValue('beslutsdatum', dayjsValue, true);
                              await setFieldTouched('beslutsdatum');
                              await validateField('beslutsdatum');
                            }}
                            onSelectedSectionsChange={() => {
                              validateField('beslutsdatum');
                            }}
                            disableFuture={true}
                          />
                        </div>
                        <p className="tw-mt-1 tw-h-4 tw-text-raa-error-red">
                          {errors.beslutsdatum && touched.beslutsdatum && errors.beslutsdatum}
                        </p>
                      </div>
                      <div className="tw-w-1/2">
                        <RaaInputField
                          name="diarienummer"
                          label="Diarienummer"
                          placeholder=""
                          shouldValidateForm={true}
                        />
                      </div>
                    </fieldset>
                  </DrawerSection>
                </DrawerBody>
                <DrawerFooter>
                  <button
                    className="raa-button raa-button-cancel tw-flex-1 md:tw-flex-none"
                    type="button"
                    onClick={() => navigate('/', { replace: true })}
                  >
                    avbryt
                  </button>
                  <button
                    className="raa-button raa-button-confirm tw-flex-1 md:tw-w-32 md:tw-flex-none"
                    type="submit"
                    form="skyddsklassningForm"
                    disabled={aktivitetRequestStatus === 'pending' || !dirtyForm || !isValid}
                  >
                    {aktivitetRequestStatus === 'pending' ? <RaaSpinner size="2rem" /> : 'Spara'}
                  </button>
                </DrawerFooter>
              </DrawerContainer>
            </Form>
          )}
        </Formik>
      )}
    </SpinnerContainer>
  );
};

export default SkyddsklassningRegistration;
